// Interprets the JSON Locator types from ADR-0002 against a ManuscriptDocument.
// Returns up to a fixed number of matches per locator to keep output bounded.

import type { Locator } from '../checklists/types';
import type { ManuscriptDocument, SectionName, SectionSpan } from '../pdf/types';
import type { LocatorMatch } from './types';

const MAX_MATCHES_PER_LOCATOR = 5;

interface SectionSlice {
  section: SectionName;
  start: number;
  end: number;
  text: string;
}

function sliceSection(doc: ManuscriptDocument, section: SectionName | undefined): SectionSlice[] {
  if (!section) {
    return [{ section: 'unknown', start: 0, end: doc.rawText.length, text: doc.rawText }];
  }
  if (section === 'title') {
    // Use the heuristic title (first substantial line above the abstract) when present.
    if (doc.title) {
      const offset = doc.rawText.indexOf(doc.title);
      return [
        {
          section: 'title',
          start: offset >= 0 ? offset : 0,
          end: offset >= 0 ? offset + doc.title.length : doc.title.length,
          text: doc.title,
        },
      ];
    }
    return [];
  }
  const spans = doc.sections.filter((s: SectionSpan) => s.section === section);
  return spans.map((s) => ({
    section: s.section,
    start: s.start,
    end: s.end,
    text: doc.rawText.slice(s.start, s.end),
  }));
}

export function findLocatorMatches(loc: Locator, doc: ManuscriptDocument): LocatorMatch[] {
  switch (loc.type) {
    case 'regex': {
      const slices = sliceSection(doc, loc.in_section);
      const matches: LocatorMatch[] = [];
      const flags = (loc.flags ?? '').includes('g') ? loc.flags ?? 'g' : (loc.flags ?? '') + 'g';
      let regex: RegExp;
      try {
        regex = new RegExp(loc.pattern, flags);
      } catch {
        return [];
      }
      for (const slice of slices) {
        regex.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = regex.exec(slice.text)) !== null && matches.length < MAX_MATCHES_PER_LOCATOR) {
          matches.push({
            offset: slice.start + m.index,
            text: m[0].slice(0, 200),
            section: slice.section,
            via: 'regex',
          });
          if (m[0].length === 0) regex.lastIndex++;
        }
      }
      if ((loc.min_matches ?? 1) > matches.length) return [];
      return matches;
    }
    case 'keyword_phrase': {
      const slices = sliceSection(doc, loc.in_section);
      const matches: LocatorMatch[] = [];
      for (const slice of slices) {
        const lower = slice.text.toLowerCase();
        for (const phrase of loc.phrases) {
          const needle = phrase.toLowerCase();
          let from = 0;
          while (matches.length < MAX_MATCHES_PER_LOCATOR) {
            const idx = lower.indexOf(needle, from);
            if (idx < 0) break;
            const ctx = excerpt(slice.text, idx, needle.length);
            matches.push({
              offset: slice.start + idx,
              text: ctx,
              section: slice.section,
              via: 'keyword_phrase',
            });
            from = idx + needle.length;
          }
        }
      }
      return matches;
    }
    case 'section_present': {
      const slices = sliceSection(doc, loc.section);
      if (slices.length === 0) return [];
      const slice = slices[0];
      return [
        {
          offset: slice.start,
          text: excerpt(slice.text, 0, Math.min(120, slice.text.length)),
          section: slice.section,
          via: 'section_present',
        },
      ];
    }
    case 'table_present': {
      // Sprint 1's extractor doesn't surface tables explicitly. Fall back to
      // detecting "Table N" / "table N" mentions with a caption-pattern filter.
      const re = /\btable\s+\d+\s*[.:]?\s*([^\n]{0,160})/gi;
      const captionRe = loc.caption_pattern
        ? safeRegex(loc.caption_pattern, 'i')
        : null;
      const matches: LocatorMatch[] = [];
      let m: RegExpExecArray | null;
      while ((m = re.exec(doc.rawText)) !== null && matches.length < MAX_MATCHES_PER_LOCATOR) {
        const caption = m[1] ?? '';
        if (captionRe && !captionRe.test(caption)) continue;
        matches.push({
          offset: m.index,
          text: m[0].slice(0, 200),
          section: sectionAtForDoc(doc, m.index),
          via: 'table_present',
        });
      }
      return matches;
    }
    case 'number_with_unit': {
      const slices = sliceSection(doc, loc.in_section);
      const re = safeRegex(`(\\d+\\.?\\d*)\\s*${loc.unit_pattern}`, 'gi');
      if (!re) return [];
      const matches: LocatorMatch[] = [];
      for (const slice of slices) {
        re.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = re.exec(slice.text)) !== null && matches.length < MAX_MATCHES_PER_LOCATOR) {
          matches.push({
            offset: slice.start + m.index,
            text: m[0].slice(0, 200),
            section: slice.section,
            via: 'number_with_unit',
          });
        }
      }
      return matches;
    }
    case 'composite_or': {
      const matches: LocatorMatch[] = [];
      for (const sub of loc.locators) {
        for (const m of findLocatorMatches(sub, doc)) {
          matches.push(m);
          if (matches.length >= MAX_MATCHES_PER_LOCATOR) return matches;
        }
      }
      return matches;
    }
    case 'composite_and': {
      // All sub-locators must produce ≥1 match; return the union (capped).
      const allMatches: LocatorMatch[][] = [];
      for (const sub of loc.locators) {
        const subMatches = findLocatorMatches(sub, doc);
        if (subMatches.length === 0) return [];
        allMatches.push(subMatches);
      }
      const merged: LocatorMatch[] = [];
      for (const ms of allMatches) {
        for (const m of ms) {
          merged.push(m);
          if (merged.length >= MAX_MATCHES_PER_LOCATOR) return merged;
        }
      }
      return merged;
    }
  }
}

function excerpt(text: string, idx: number, len: number): string {
  const before = Math.max(0, idx - 30);
  const after = Math.min(text.length, idx + len + 30);
  return text.slice(before, after).trim().slice(0, 200);
}

function sectionAtForDoc(doc: ManuscriptDocument, offset: number): SectionName {
  for (const s of doc.sections) {
    if (offset >= s.start && offset < s.end) return s.section;
  }
  return 'unknown';
}

function safeRegex(pattern: string, flags: string): RegExp | null {
  try {
    return new RegExp(pattern, flags);
  } catch {
    return null;
  }
}
